"use client";

import { useState } from "react";
import useSWR from "swr";
import { DocumentCard } from "./document-card";
import type { Document } from "@/lib/db/schema";
import { apiClient } from "@/lib/api-client";
import type { FilterId } from "@/components/collections/collection-list";

const fetcher = <T,>(url: string): Promise<T> =>
  apiClient.get<T>(url).then((r) => r.data);

type DocWithCollections = Document & {
  collections: { id: string; name: string; icon: string | null }[];
};

interface DocumentListData {
  owned: DocWithCollections[];
  shared: (DocWithCollections & { role: string })[];
}

interface DocumentListProps {
  filter?: FilterId;
}

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
const FALLBACK_LIMIT = 5;

function applyTimeFilterWithFallback(
  docs: DocWithCollections[],
  getTime: (d: DocWithCollections) => number,
  now: number
): DocWithCollections[] {
  const withinWindow = docs.filter((d) => now - getTime(d) < SEVEN_DAYS);
  if (withinWindow.length > 0) return withinWindow;
  // Fallback: 5 most recent regardless of timeframe
  return [...docs]
    .sort((a, b) => getTime(b) - getTime(a))
    .slice(0, FALLBACK_LIMIT);
}

export function DocumentList({ filter = "__all" }: DocumentListProps) {
  const { data, mutate } = useSWR<DocumentListData>("/api/documents", fetcher);

  const handleArchive = async (id: string) => {
    await apiClient.delete(`/api/documents/${id}`);
    mutate();
  };

  const handleRename = async (id: string, title: string) => {
    await apiClient.patch(`/api/documents/${id}`, { title });
    mutate();
  };

  const owned = data?.owned ?? [];
  const shared = data?.shared ?? [];

  // Apply filter.
  //
  // The clock is sampled once per mount via a lazy initialiser rather than read
  // during render — `Date.now()` in a render body is impure, so the 7-day
  // buckets below could silently reshuffle on any unrelated re-render. Pinning
  // it is also what the UI already implies: the window only has to be accurate
  // to the day, and the page is remounted long before drift is visible.
  const [now] = useState(() => Date.now());
  let filteredOwned: DocWithCollections[];

  if (filter === "__all") {
    filteredOwned = owned;
  } else if (filter === "__new") {
    filteredOwned = applyTimeFilterWithFallback(
      owned,
      (d) => new Date(d.createdAt).getTime(),
      now
    );
  } else if (filter === "__recent") {
    filteredOwned = applyTimeFilterWithFallback(
      owned,
      (d) => new Date(d.updatedAt).getTime(),
      now
    );
  } else if (filter === "__uncollected") {
    filteredOwned = owned.filter((d) => d.collections.length === 0);
  } else {
    filteredOwned = owned.filter((d) =>
      d.collections.some((c) => c.id === filter)
    );
  }

  return (
    <div className="space-y-8">
      {filteredOwned.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">My Documents</h2>
          <div className="column-container with-spacing">
            {filteredOwned.map((doc) => (
              <DocumentCard
                key={doc.id}
                document={doc}
                onArchive={handleArchive}
                onRename={handleRename}
              />
            ))}
          </div>
        </div>
      )}

      {shared.length > 0 && filter === "__all" && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Shared with Me</h2>
          <div className="column-container with-spacing">
            {shared.map((doc) => (
              <DocumentCard key={doc.id} document={doc} />
            ))}
          </div>
        </div>
      )}

      {filteredOwned.length === 0 && (shared.length === 0 || filter !== "__all") && data && (
        <div className="text-center py-16 text-muted-foreground">
          <p className="text-lg">
            {filter === "__all"
              ? "No documents yet"
              : "No documents match this filter"}
          </p>
          <p className="text-sm mt-1">
            {filter === "__all"
              ? "Create a new document or start from a template"
              : "Try a different filter or create a new document"}
          </p>
        </div>
      )}
    </div>
  );
}
